import { emptyState } from "./data";
import type { AccountProfile, FarmstandState, FarmstandSummary } from "./types";

const STORAGE_KEY = "farmstand-workspace-v4";
const LEGACY_STORAGE_KEY = "farmstand-state-v3";

export interface FarmstandWorkspaceCache {
  account: AccountProfile | null;
  farmstands: FarmstandSummary[];
  activeStandId: string | null;
  farmstandsById: Record<string, FarmstandState>;
}

const emptyWorkspaceCache: FarmstandWorkspaceCache = {
  account: null,
  farmstands: [],
  activeStandId: null,
  farmstandsById: {}
};

export function createId(prefix: string) {
  if ("crypto" in window && "randomUUID" in window.crypto) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

export function loadFarmstandWorkspace(): FarmstandWorkspaceCache {
  const workspace = parseWorkspace(window.localStorage.getItem(STORAGE_KEY));

  if (workspace) {
    return workspace;
  }

  const legacyState = parseFarmstand(window.localStorage.getItem(LEGACY_STORAGE_KEY));

  if (!legacyState?.settings.name) {
    return emptyWorkspaceCache;
  }

  const summary = summarizeFarmstand(legacyState);

  return {
    account: legacyState.account,
    farmstands: [summary],
    activeStandId: null,
    farmstandsById: {
      [legacyState.id]: legacyState
    }
  };
}

export function saveFarmstandWorkspace(workspace: FarmstandWorkspaceCache) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(workspace));
}

export function clearFarmstandState() {
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_STORAGE_KEY);
}

function parseWorkspace(rawWorkspace: string | null): FarmstandWorkspaceCache | null {
  if (!rawWorkspace) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawWorkspace) as Partial<FarmstandWorkspaceCache>;
    const farmstandsById = Object.fromEntries(
      Object.entries(parsed.farmstandsById ?? {})
        .map(([, value]) => parseFarmstandValue(value))
        .filter((farmstand): farmstand is FarmstandState => Boolean(farmstand?.id))
        .map((farmstand) => [farmstand.id, farmstand])
    );
    const cachedSummaries = (parsed.farmstands ?? []).filter(
      (farmstand) => farmstand.id && farmstand.name && (farmstand.role === "owner" || farmstand.role === "contributor")
    );
    const farmstands = cachedSummaries.length
      ? cachedSummaries
      : Object.values(farmstandsById).map(summarizeFarmstand);
    return {
      account: parsed.account ?? null,
      farmstands,
      activeStandId: null,
      farmstandsById
    };
  } catch {
    return null;
  }
}

function parseFarmstand(rawFarmstand: string | null): FarmstandState | null {
  if (!rawFarmstand) {
    return null;
  }

  try {
    return parseFarmstandValue(JSON.parse(rawFarmstand) as Partial<FarmstandState>);
  } catch {
    return null;
  }
}

function parseFarmstandValue(parsed: Partial<FarmstandState>): FarmstandState | null {
  const legacyOwner = parsed.contributors?.find(
    (contributor) => contributor.name === parsed.settings?.ownerName
  );
  const id = parsed.id || (parsed.settings?.slug ? `stand-${parsed.settings.slug}` : "");

  if (!id) {
    return null;
  }

  return {
    id,
    account: parsed.account ?? emptyState.account,
    access:
      parsed.access ??
      (parsed.account && parsed.settings?.name && legacyOwner
        ? {
            role: "owner",
            memberId: legacyOwner.id,
            memberName: legacyOwner.name
          }
        : emptyState.access),
    settings: {
      ...emptyState.settings,
      ...parsed.settings,
      paymentMethods: {
        ...emptyState.settings.paymentMethods,
        ...parsed.settings?.paymentMethods
      },
      brand: {
        ...emptyState.settings.brand,
        ...parsed.settings?.brand
      }
    },
    contributors: (parsed.contributors ?? emptyState.contributors).map((contributor) => ({
      ...contributor,
      role: contributor.role ?? (contributor.id === legacyOwner?.id ? "owner" : "contributor")
    })),
    invitations: parsed.invitations ?? emptyState.invitations,
    inventory: parsed.inventory ?? emptyState.inventory,
    orders: parsed.orders ?? emptyState.orders,
    contributorSales: parsed.contributorSales ?? emptyState.contributorSales
  };
}

function summarizeFarmstand(farmstand: FarmstandState): FarmstandSummary {
  return {
    id: farmstand.id,
    name: farmstand.settings.name,
    slug: farmstand.settings.slug,
    role: farmstand.access?.role ?? "contributor"
  };
}
